import * as Yup from 'yup'
import { IMaterialEmissionFactorForm } from './types'

// ProductTypeEFEnum.PackagingMaterial. 4, not 1 or 2: those keys are legacy rows the BE enum no
// longer declares. Every packaging material emission factor carries this value.
export const PRODUCT_TYPE_PACKAGING_MATERIAL = 4

export enum TabsEnum {
  MATERIAL = 'MATERIAL',
  DATA_ORIGIN = 'DATA_ORIGIN',
  EMISSIONS = 'EMISSIONS',
  IMPACTS = 'IMPACTS',
  DATA_QUALITY = 'DATA_QUALITY',
}

// Story 36911's tab list. Losses and Activity data are absent on purpose: both are farm-shaped and
// do not apply to a packaging material (spec OQ-A).
export const TAB_LABELS = {
  [TabsEnum.MATERIAL]: 'Material info',
  [TabsEnum.DATA_ORIGIN]: 'Data origin',
  [TabsEnum.EMISSIONS]: 'GHG Emissions',
  [TabsEnum.IMPACTS]: 'Impacts',
  [TabsEnum.DATA_QUALITY]: 'Data quality',
}

export const TAB_ERRORS = {
  [TabsEnum.MATERIAL]: [
    'packagingMaterialCategoryId',
    'productId',
    'isRecycled',
    'functionalUnit',
    'countryId',
    'regionIdentifierId',
  ],
  [TabsEnum.DATA_ORIGIN]: ['dataOrigin', 'externalSource', 'dataOriginComment', 'proxyNote'],
  [TabsEnum.EMISSIONS]: ['carbon'],
  [TabsEnum.IMPACTS]: [
    'landUse',
    'biodiversity',
    'eutrophication',
    'waterUsage',
    'waterUsageIncludingWasterScarcity',
  ],
  [TabsEnum.DATA_QUALITY]: [
    'secondaryDataReferenceSourceType',
    'referencesSamplingYear',
    'referenceDataPublicationYear',
    'referenceSourcingCountry',
    'referenceRegionIdentifier',
    'referenceTechnologyType',
    'referenceTechnologyProxyContribution',
  ],
}

const optionalNonNegative = (label: string) =>
  Yup.number()
    .nullable()
    .transform((value, original) => (original === '' || original === null ? null : value))
    .typeError(`${label} must be a number`)
    .min(0, `${label} must be greater than, or equal to 0`)

const optionalNumber = (label: string) =>
  Yup.number()
    .nullable()
    .transform((value, original) => (original === '' || original === null ? null : value))
    .typeError(`${label} must be a number`)

// Required set per 36911: Category, Material name, Virgin/Recycled, Functional unit and Original
// Net Carbon. Everything else is optional, including every FLAG field and the Proxy note - saving
// with those empty must not error, which is an acceptance criterion.
export const formSchema = Yup.object().shape({
  packagingMaterialCategoryId: Yup.number().nullable().required('Category is required'),
  productId: Yup.number().nullable().required('Material name is required'),
  isRecycled: Yup.boolean().nullable().required('Virgin/Recycled is required'),
  functionalUnit: Yup.number().nullable().required('Functional unit is required'),
  carbon: Yup.number()
    .nullable()
    .transform((value, original) => (original === '' || original === null ? null : value))
    .typeError('Original Net Carbon Emissions must be a number')
    .required('Original Net Carbon Emissions is required'),
  landUse: optionalNonNegative('Land Use'),
  biodiversity: optionalNonNegative('Biodiversity'),
  eutrophication: optionalNonNegative('Eutrophication'),
  waterUsage: optionalNumber('Freshwater Withdrawal'),
  waterUsageIncludingWasterScarcity: optionalNumber('Freshwater accounting for Water Scarcity'),
  referenceTechnologyProxyContribution: Yup.number()
    .nullable()
    .transform((value, original) => (original === '' || original === null ? null : value))
    .min(0, 'Reference Technology Proxy Contribution must be between 0 and 100')
    .max(100, 'Reference Technology Proxy Contribution must be between 0 and 100'),
  dataOriginComment: Yup.string().nullable().max(512, 'Data origin comment is 512 characters max'),
  proxyNote: Yup.string().nullable().max(512, 'Proxy note is 512 characters max'),
})

// The BE stores the material taxonomy leaf id directly, but the form lets the user pick a Category
// first and filters Material name by it. Sending a material that no longer belongs to the selected
// category would be rejected by the packaging rules, so the pair is cleared together upstream.
export const toNumberOrNull = (value: unknown): number | null =>
  value === '' || value === null || value === undefined || Number(value) < 0 ? null : Number(value)

// Category is form state derived from the material leaf, so it is null on the loaded record and
// set on the form. Comparing it would leave the page permanently dirty - the "save before
// publishing" banner up and Publish disabled - even straight after a save. Entries are sorted
// because the form object is rebuilt by spreading, which does not preserve the record's key order.
const toComparableWireShape = (source: IMaterialEmissionFactorForm): string => {
  const { packagingMaterialCategoryId, ...wireFields } = source
  const entries = Object.entries(wireFields as unknown as Record<string, unknown>)
  return JSON.stringify(entries.sort(([a], [b]) => a.localeCompare(b)))
}

// True when the form holds edits the record does not, ignoring the UI-only Category.
export const hasUnsavedChanges = (
  record: IMaterialEmissionFactorForm,
  values: IMaterialEmissionFactorForm
): boolean => toComparableWireShape(record) !== toComparableWireShape(values)

// Mirrors food's src/modules/emission-factors/emission/utils.ts FLAG_FIELDS/getFLAGCarbonValue.
export const FLAG_FIELDS = [
  'flag_L1_LandManagementCarbon',
  'flag_L1_LandManagementNonCarbon',
  'flag_L1_LandUseChangeCarbon',
  'flag_L1_NonLandCarbon',
]

export const getFLAGCarbonValue = (data: IMaterialEmissionFactorForm) => {
  const record = data as unknown as Record<string, unknown>

  return FLAG_FIELDS.every(item => record[item] === null)
    ? null
    : FLAG_FIELDS.reduce((acc, field) => {
        const fieldValue = record[field]

        if (!fieldValue) {
          return acc
        }

        const value = parseFloat(fieldValue as string)

        return value ? Number((acc + value).toPrecision(12)) : acc
      }, 0)
}
